<template>
  <div class="md-layout product-form-page">
    <div class="md-layout-item">

      <md-card class="form-card" v-if="productDetail._id">
        <md-card-header class="form-card-header" :class="headerClass">
          <div class="header-icon-wrap">
            <md-icon>edit</md-icon>
          </div>
          <div class="header-text">
            <h4 class="title">Edit Product</h4>
            <p class="subtitle">{{ productDetail.displayName || productDetail.name }}</p>
          </div>
          <div class="type-badge-header">
            <span class="type-pill">{{ productDetail.productType }}</span>
          </div>
        </md-card-header>

        <md-card-content class="form-body">

          <!-- ── Basic Info ── -->
          <div class="form-section">
            <h5 class="section-title">
              <md-icon>info_outline</md-icon> Basic Information
            </h5>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Product Name <span class="required">*</span></label>
                <input v-model="productDetail.name" class="field-input" />
              </div>
              <div class="form-group">
                <label class="field-label">Display Name <span class="required">*</span></label>
                <input v-model="productDetail.displayName" class="field-input" />
              </div>
            </div>

            <div class="form-group">
              <label class="field-label">Short Description</label>
              <textarea v-model="productDetail.shortDescription" class="field-input field-textarea" rows="3" />
            </div>
          </div>

          <!-- ── Accessories: Parent Products ── -->
          <div class="form-section" v-if="productDetail.productType === 'Accessories'">
            <h5 class="section-title">
              <md-icon>link</md-icon> Compatible Products
            </h5>
            <p class="section-desc">Select which one-time products this accessory belongs to.</p>

            <div v-if="onetimeProducts.length === 0" class="info-banner">
              <md-icon>info_outline</md-icon> No one-time products found.
            </div>

            <div class="parent-products-grid">
              <label
                v-for="p in onetimeProducts"
                :key="p._id"
                class="parent-product-card"
                :class="{ selected: (productDetail.parentProductIds || []).map(String).includes(String(p._id)) }"
              >
                <input
                  type="checkbox"
                  :value="p._id"
                  v-model="productDetail.parentProductIds"
                  style="display:none"
                />
                <div class="pp-avatar">{{ (p.name || '?')[0].toUpperCase() }}</div>
                <div class="pp-info">
                  <strong>{{ p.displayName || p.name }}</strong>
                  <small>{{ p.name }}</small>
                </div>
                <md-icon class="pp-check">
                  {{ (productDetail.parentProductIds || []).map(String).includes(String(p._id)) ? 'check_circle' : 'radio_button_unchecked' }}
                </md-icon>
              </label>
            </div>
          </div>

          <!-- ── Specifications ── -->
          <div class="form-section">
            <h5 class="section-title">
              <md-icon>list_alt</md-icon> Product Specifications
            </h5>

            <div v-if="(productDetail.productSpecifications || []).length === 0" class="empty-specs">
              <md-icon>list_alt</md-icon>
              <p>No specifications yet. Click "Add" to start.</p>
            </div>

            <div v-for="(spec, i) in productDetail.productSpecifications" :key="i" class="spec-row">
              <input v-model="spec.key" class="field-input spec-input" placeholder="Key (e.g. Battery)" />
              <input v-model="spec.value" class="field-input spec-input" placeholder="Value (e.g. 5000 mAh)" />
              <button class="remove-spec-btn" @click="removeSpecification(i)">
                <md-icon>delete_outline</md-icon>
              </button>
            </div>

            <button class="add-spec-btn" @click="addSpecification">
              <md-icon>add</md-icon> Add Specification
            </button>
          </div>

          <!-- ── Save Button ── -->
          <div class="form-actions">
            <button class="btn-cancel" @click="$router.push('/kid/wingio-products')">
              Cancel
            </button>
            <button class="btn-save" @click="updateProduct" :disabled="isDisabled">
              <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i> Saving…</span>
              <span v-else><md-icon>save</md-icon> Save Changes</span>
            </button>
          </div>

        </md-card-content>
      </md-card>

      <!-- Loading skeleton -->
      <div v-else class="loading-wrap">
        <md-progress-spinner md-mode="indeterminate" :md-diameter="40" :md-stroke="3" />
      </div>

    </div>
  </div>
</template>

<script>
import OrdersDataService from '../../services/SuperAdmin/Kids/OrdersDataService';

export default {
  data() {
    return {
      isDisabled: false,
      productdId: this.$route.query.id,
      onetimeProducts: [],
      productDetail: {
        _id: '',
        name: '',
        displayName: '',
        shortDescription: '',
        productType: 'Onetime',
        productSpecifications: [],
        parentProductIds: [],
      },
    };
  },

  computed: {
    headerClass() {
      const map = {
        Onetime: 'header-onetime',
        Subscription: 'header-subscription',
        'Lifetime Subscription': 'header-lifetime',
        Accessories: 'header-accessories',
      };
      return map[this.productDetail.productType] || 'header-onetime';
    },
  },

  methods: {
    loadProduct() {
      OrdersDataService.getWingioProductImages({ id: this.productdId })
        .then((response) => {
          this.productDetail = {
            ...response.data.produtDetail,
            productSpecifications: response.data.produtDetail.productSpecifications || [],
            parentProductIds: (response.data.produtDetail.parentProductIds || []).map(String),
          };
          if (this.productDetail.productType === 'Accessories') {
            this.loadOnetimeProducts();
          }
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.respones_msg || 'Failed to load product');
        });
    },
    loadOnetimeProducts() {
      OrdersDataService.getOnetimeProducts()
        .then((r) => { this.onetimeProducts = r.data.data; })
        .catch(() => {});
    },
    addSpecification() {
      if (!this.productDetail.productSpecifications) this.productDetail.productSpecifications = [];
      this.productDetail.productSpecifications.push({ key: '', value: '' });
    },
    removeSpecification(i) {
      this.productDetail.productSpecifications.splice(i, 1);
    },
    updateProduct() {
      if (!this.productDetail.name.trim() || !this.productDetail.displayName.trim()) {
        this.$toast.error('Name and Display Name are required.');
        return;
      }
      this.isDisabled = true;
      OrdersDataService.updateProductDetails(this.productDetail)
        .then(() => {
          this.$toast.success('Product updated successfully!');
          this.isDisabled = false;
        })
        .catch((e) => {
          this.$toast.error(e.response?.data?.respones_msg || 'Update failed.');
          this.isDisabled = false;
        });
    },
  },

  beforeMount() {
    this.loadProduct();
  },
};
</script>

<style scoped>
.product-form-page { font-family: 'Segoe UI', sans-serif; }
.form-card { border-radius: 14px; overflow: hidden; box-shadow: 0 4px 20px rgba(0,0,0,0.08); }

/* Header variants */
.form-card-header {
  display: flex; align-items: center; gap: 16px;
  padding: 22px 28px; color: white;
}
.header-onetime { background: linear-gradient(135deg, #43a047, #1b5e20); }
.header-subscription { background: linear-gradient(135deg, #1976d2, #0d47a1); }
.header-lifetime { background: linear-gradient(135deg, #f57c00, #bf360c); }
.header-accessories { background: linear-gradient(135deg, #8e24aa, #4a148c); }

.header-icon-wrap {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.header-icon-wrap .md-icon { color: white !important; font-size: 24px !important; }
.header-text { flex: 1; }
.form-card-header .title { color: white; margin: 0; font-size: 18px; font-weight: 600; }
.form-card-header .subtitle { color: rgba(255,255,255,0.75); margin: 2px 0 0; font-size: 13px; }
.type-pill {
  padding: 4px 14px; border-radius: 20px;
  background: rgba(255,255,255,0.2); color: white;
  font-size: 12px; font-weight: 600; letter-spacing: 0.5px; text-transform: uppercase;
  border: 1px solid rgba(255,255,255,0.3);
}

/* Form body */
.form-body { padding: 0; }
.form-section {
  padding: 24px 28px 20px;
  border-bottom: 1px solid #f0f0f0;
}
.form-section:last-of-type { border-bottom: none; }

.section-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 14px; font-weight: 600; color: #444; margin: 0 0 16px;
}
.section-title .md-icon { font-size: 18px !important; color: #888 !important; }
.section-desc { font-size: 13px; color: #888; margin: -8px 0 16px; }

/* Form fields (shared with Create) */
.form-row { display: flex; gap: 16px; }
.form-row.two-col > * { flex: 1; }
.form-group { margin-bottom: 16px; flex: 1; }
.field-label { display: block; font-size: 11px; font-weight: 700; color: #666; margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.5px; }
.required { color: #e53935; }
.field-input {
  width: 100%; padding: 9px 12px; border: 1.5px solid #e0e0e0;
  border-radius: 8px; font-size: 14px; color: #333;
  outline: none; transition: border-color 0.2s; box-sizing: border-box; background: white;
}
.field-input:focus { border-color: #43a047; }
.field-textarea { resize: vertical; min-height: 80px; font-family: inherit; }

/* Parent products */
.info-banner {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; background: #e3f2fd; border-radius: 8px;
  font-size: 14px; color: #1565c0; margin-bottom: 12px;
}
.parent-products-grid { display: flex; flex-direction: column; gap: 8px; max-height: 320px; overflow-y: auto; }
.parent-product-card {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 15px; border: 1.5px solid #e0e0e0; border-radius: 10px;
  cursor: pointer; transition: all 0.15s; background: white;
}
.parent-product-card:hover { border-color: #9c27b0; background: #fdf3ff; }
.parent-product-card.selected { border-color: #9c27b0; background: #f3e5f5; }
.pp-avatar {
  width: 36px; height: 36px; border-radius: 8px;
  background: #4caf50; color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 700; flex-shrink: 0;
}
.pp-info { flex: 1; }
.pp-info strong { display: block; font-size: 14px; color: #333; }
.pp-info small { color: #888; font-size: 12px; }
.pp-check { color: #9c27b0 !important; font-size: 20px !important; }

/* Specs */
.empty-specs { text-align: center; padding: 24px; color: #ccc; }
.empty-specs .md-icon { font-size: 32px !important; color: #ddd !important; display: block; margin-bottom: 6px; }
.empty-specs p { margin: 0; font-size: 13px; }
.spec-row { display: flex; gap: 10px; align-items: center; margin-bottom: 10px; }
.spec-input { flex: 1; }
.remove-spec-btn {
  background: none; border: none; cursor: pointer; padding: 6px;
  color: #e53935; border-radius: 6px; transition: background 0.15s; flex-shrink: 0;
}
.remove-spec-btn:hover { background: #ffebee; }
.remove-spec-btn .md-icon { color: #e53935 !important; font-size: 18px !important; }
.add-spec-btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border: 1.5px dashed #43a047;
  background: transparent; border-radius: 8px;
  color: #43a047; font-size: 13px; font-weight: 600; cursor: pointer; margin-top: 4px;
}
.add-spec-btn:hover { background: #e8f5e9; }
.add-spec-btn .md-icon { color: #43a047 !important; font-size: 17px !important; }

/* Actions */
.form-actions {
  display: flex; justify-content: flex-end; gap: 12px;
  padding: 20px 28px 28px;
}
.btn-cancel {
  padding: 10px 22px; border-radius: 9px; border: 1.5px solid #e0e0e0;
  background: white; color: #666; font-size: 14px; font-weight: 600; cursor: pointer;
}
.btn-cancel:hover { background: #f5f5f5; }
.btn-save {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 24px; border-radius: 9px; border: none;
  background: #43a047; color: white; font-size: 14px; font-weight: 600; cursor: pointer;
  transition: background 0.15s;
}
.btn-save:hover:not(:disabled) { background: #388e3c; }
.btn-save:disabled { opacity: 0.65; cursor: not-allowed; }
.btn-save .md-icon { color: white !important; font-size: 18px !important; }

.loading-wrap { display: flex; justify-content: center; padding: 80px; }
</style>